import { useGlobalQuery } from 'state/query-client/hooks';
import { getDatabaseConnection, DatabaseConnection } from 'services/database-connection-service';
import { useConfig } from './use-config';

/**
 * Custom hook to fetch a single database connection by ID
 *
 * This hook provides a query function to fetch a specific database connection.
 * It uses the global query pattern for consistent error handling and authentication management.
 *
 * Features:
 * - Automatic configuration injection
 * - Global error handling with authentication management
 * - Loading state management
 * - Caching and refetching support
 * - TypeScript support for response types
 *
 * @param connectionId - The ID of the database connection to fetch
 * @returns {Object} The query object with data and state
 * @returns {DatabaseConnection | undefined} data - The database connection data
 * @returns {boolean} isLoading - Whether the query is loading
 * @returns {boolean} isSuccess - Whether the query completed successfully
 * @returns {boolean} isError - Whether the query failed
 * @returns {Error | null} error - The error if query failed
 * @returns {Function} refetch - Function to manually refetch the data
 *
 * @example
 * const { data: connection, isLoading } = useGetDatabaseConnection(connectionId);
 * 
 * if (isLoading) {
 *   return <LoadingSpinner />;
 * }
 * 
 * return (
 *   <div>
 *     <h3>{connection?.displayName}</h3>
 *     <p>Host: {connection?.connectionInfo.host}</p>
 *   </div>
 * );
 */
export const useGetDatabaseConnection = (connectionId?: string) => {
    const { configurationSettings } = useConfig();

    return useGlobalQuery<DatabaseConnection>({
        queryKey: ['getDatabaseConnection', connectionId],
        queryFn: async () => {
            if (!configurationSettings) {
                throw new Error('Configuration settings are not available');
            }
            if (!connectionId) {
                throw new Error('Connection ID is required');
            }
            return await getDatabaseConnection(configurationSettings, connectionId);
        },
        enabled: !!configurationSettings && !!connectionId,
        staleTime: 30000, // 30 seconds
        refetchOnMount: true,
        refetchOnWindowFocus: false,
    });
};

